// we expect to be redirect here after a signin/up event, we'll query the users/me endpoint
// to fetch the full user and redirect to the dashboard
import React, { useEffect, useState } from "react";

import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { useSearchParams } from "react-router-dom";

import useDetectedLogin from "@app/analytics/hooks/sso/useDetectedLogin";
import * as Events from "@app/analytics/tracking/events";
import useAuth, { User } from "@app/common/auth/useAuth";
import FullPageLoadingSpinnerWithText from "@app/common/components/FullPageLoadingSpinnerWithText";
import ROUTES from "@app/common/routes";
import { LocationWithState } from "@app/common/types";
import type { SSOProviderId } from "@app/common/types/providerTypes";
import {
  getRedirectTo,
  parseUrlWithParamsUrl,
  withQueryParams,
  withQueryParamsUrl,
} from "@app/common/utils";

interface RedirectFuncParams {
  user?: User | null;
  skipOnboarding?: boolean | null;
  queryParams: URLSearchParams;
}

const ACTION_TO_URL: {
  [key: string]: string | ((opts: RedirectFuncParams) => string);
} = {
  default: ROUTES.LANDING_PAGE,
  signup: ({ user, skipOnboarding, queryParams }) => {
    // do this check here as well since it's not unreasonable for someone with
    // an OAuth account to sign in from the signup page
    if (!user?.userMetadata?.onboardingComplete && !skipOnboarding) {
      return withQueryParamsUrl(
        ROUTES.WELCOME,
        { action: "signup", method: queryParams.get("method") },
        queryParams,
        ["p", "email"] // remove query params appended during the signup flow
      );
    }

    const redirectTo = getRedirectTo(queryParams);
    const zoomRedirect = queryParams.get("zoomRedirect");
    return redirectTo || zoomRedirect || ROUTES.LANDING_PAGE;
  },
  signin: ({ user, skipOnboarding, queryParams }) => {
    if (!user?.userMetadata?.onboardingComplete && !skipOnboarding) {
      return withQueryParamsUrl(
        ROUTES.WELCOME,
        { action: "signin", method: queryParams.get("method") },
        queryParams
      );
    }

    const redirectTo = getRedirectTo(queryParams);
    const zoomRedirect = queryParams.get("zoomRedirect");
    return redirectTo || zoomRedirect || ROUTES.LANDING_PAGE;
  },
  calendarSuccess: `${ROUTES.USER_SETTINGS_INTEGRATIONS}?success=true`,
  calendarError: `${ROUTES.USER_SETTINGS_INTEGRATIONS}?error=true`,
  nouser: ROUTES.REAUTH_REQUIRED,
};

const getUrlFromAction = (
  action: string,
  redirectOptions: RedirectFuncParams
) => {
  const urlOrFunc = ACTION_TO_URL[action] || ACTION_TO_URL["default"];
  if (typeof urlOrFunc === "string") {
    return urlOrFunc;
  }
  return urlOrFunc(redirectOptions);
};

const AuthReturn = () => {
  const { fetchAndSetUser, user, hasPreviousSignIn } = useAuth();
  const [queryParams] = useSearchParams();
  const [error, setError] = useState(queryParams.get("error") === "true");
  const navigate = useNavigate();
  const location = useLocation() as LocationWithState;
  const { setPersistedLogin } = useDetectedLogin();

  const action = queryParams.get("action") || "default";
  const method =
    (queryParams.get("method") as SSOProviderId | "email") || "unknown";

  let errorRedirectPath: string | null = null;
  if (error) {
    errorRedirectPath = queryParams.get("errorRedirectTo");
  }

  const redirectPath = getUrlFromAction(action, {
    user,
    skipOnboarding:
      location.state?.skipOnboarding ||
      queryParams.get("skipOnboarding") === "true",
    queryParams,
  });

  useEffect(() => {
    if (!user) return;
    // if we are coming from the meet app, close the window
    // so the user can return to their meeting
    if (
      queryParams.get("source") === "meet" &&
      queryParams.get("method") !== "google_one_tap"
    ) {
      window.close();
    } else {
      if (
        queryParams.get("externalRedirect") === "true" &&
        !errorRedirectPath
      ) {
        window.location.href = redirectPath;
        return;
      }

      navigate(
        parseUrlWithParamsUrl(
          errorRedirectPath || redirectPath,
          location.search
        ),
        {
          replace: true,
          state: location.state,
        }
      );
    }
  }, [user]);

  useEffect(() => {
    if (action === "nouser") {
      navigate(redirectPath, { replace: true, state: location.state });
    } else if (!error) {
      Events.authEvent({ action, method });
      fetchAndSetUser().catch(setError);
    }
  }, [action]);

  useEffect(() => {
    if (!error) return;
    // if there was an error while signing in, clear out the preferred
    // login method
    setPersistedLogin(null);
  }, [error]);

  if (error) {
    return (
      <Navigate
        to={withQueryParams(
          hasPreviousSignIn ? ROUTES.SIGN_IN : ROUTES.SIGN_UP,
          {
            ...(method === "email" ? null : { p: method }),
          },
          queryParams,
          ["method"]
        )}
      />
    );
  }

  return <FullPageLoadingSpinnerWithText text="Redirecting..." delay={2000} />;
};

export default AuthReturn;
